import { Alert, Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

/**
 * KTO ZOBACZY — szkielet wyboru odbiorców.
 *
 * ┌─ DLACZEGO TRZY Z CZTERECH ZAKRESÓW SĄ „OCZEKUJE NA BACKEND" ───────────────────────────────┐
 * │ GLOBAL nie potrzebuje żadnej listy — działa.                                                │
 * │ CITY, SCHOOL i USER potrzebują KATALOGÓW z systemu głównego: miast, szkół w mieście,        │
 * │ wyszukiwarki uczniów. Panel ich nie ma, więc zamiast list zaszytych w kodzie albo pola      │
 * │ „wklej GUID" pokazuje uczciwy stan. Zakres CITY dodatkowo nie istnieje w API paska V1       │
 * │ (`MessageBarController`: GLOBAL / SCHOOL / USER).                                            │
 * │                                                                                             │
 * │ Rozstrzyganie, KTO co zobaczy (USER > SCHOOL > CITY > GLOBAL), należy do serwera. Panel go  │
 * │ nie liczy i nie udaje, że liczy.                                                            │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 */

export type Zakres = 'GLOBAL' | 'CITY' | 'SCHOOL' | 'USER';

interface OpisZakresu {
  etykieta: string;
  opis: string;
  /** `null` = zakres gotowy do użycia. */
  oczekuje: string | null;
}

const ZAKRESY: Readonly<Record<Zakres, OpisZakresu>> = {
  GLOBAL: {
    etykieta: 'Wszyscy',
    opis: 'Każdy uczeń, także niezalogowany.',
    oczekuje: null,
  },
  // Od 2026-10-02 system główny zna wszystkie cztery zakresy (pasek V2) i katalogi do wyboru
  // celu — miasta i szkoły przychodzą z systemu głównego, uczniów wskazuje wyszukiwarka.
  CITY: {
    etykieta: 'Miasta',
    opis: 'Uczniowie szkół w wybranych miastach (lista z systemu głównego).',
    oczekuje: null,
  },
  SCHOOL: {
    etykieta: 'Szkoły',
    opis: 'Uczniowie wybranych szkół — wybór zawężany miastem.',
    oczekuje: null,
  },
  USER: {
    etykieta: 'Uczniowie',
    opis: 'Wskazane osoby — z wyszukiwarki, bez wklejania identyfikatorów.',
    oczekuje: null,
  },
};

export interface KreatorOdbiorcowProps {
  zakres: Zakres;
  onZakres: (z: Zakres) => void;
  wylaczone?: boolean;
}

export function KreatorOdbiorcow({ zakres, onZakres, wylaczone = false }: KreatorOdbiorcowProps) {
  const opis = ZAKRESY[zakres];
  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={zakres}
        disabled={wylaczone}
        aria-label="Zakres odbiorców"
        onChange={(_, z: Zakres | null) => { if (z) onZakres(z); }}
        sx={{ mb: 1, flexWrap: 'wrap' }}
      >
        {(Object.keys(ZAKRESY) as Zakres[]).map((z) => (
          <ToggleButton key={z} value={z} aria-label={ZAKRESY[z].etykieta}>
            {ZAKRESY[z].etykieta}
            {ZAKRESY[z].oczekuje ? <Typography component="span" sx={{ ml: 0.5, fontSize: 10, opacity: 0.7 }}>· oczekuje</Typography> : null}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>{opis.opis}</Typography>
      {opis.oczekuje ? (
        <Alert severity="info" role="note" data-testid="zakres-oczekuje">{opis.oczekuje}</Alert>
      ) : null}
    </Box>
  );
}
